import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { Modal, Button, Spinner } from "react-bootstrap";
import { FileText } from "lucide-react";
import CommonDropdown from "../common/common_dropDown";
import uploadIcon from "../../assets/svg/upload_file.svg";
import closeAttachment from "../../assets/svg/close-red.svg";
import CommonStaticDropdown from "../common/common_static_dropdown";
import { submitVehicleFuelEntry, saveFuelAttachmentApi } from "./vehicle-api";
import { sanitizeInput, validateFileType, validateFileSize } from "../common/security-utils";

/**
 * FuelEntryModal Component
 * Handles adding fuel entries for a specific vehicle.
 * Implements VAPT security measures and optimized performance.
 */
const FuelEntryModal = ({ show, id, lastKM, onHide, onSubmit }) => {
  const apiUrl = process.env.REACT_APP_API_URL;
  const fileInputRef = useRef(null);

  const [isLoading, setIsLoading] = useState(false);

  // Form states
  const [vendor, setVendor] = useState(null);
  const [fuelType, setFuelType] = useState(null);
  const [currentKmFueling, setCurrentKmFueling] = useState("");
  const [lastKmFueling, setLastKmFueling] = useState(lastKM || "0");
  const [fuelQuantity, setFuelQuantity] = useState("");
  const [fuelPrice, setFuelPrice] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [attachmentPreview, setAttachmentPreview] = useState(null);

  const [errorMsg, setErrorMsg] = useState({});

  // Fuel Type options - Memoized for performance
  const fuelTypeOptions = useMemo(() => [
    { label: "Diesel", value: "Diesel" },
    { label: "Petrol", value: "Petrol" },
    { label: "CNG", value: "CNG" },
    { label: "LPG", value: "LPG" },
  ], []);

  // Sync lastKM prop
  useEffect(() => {
    setLastKmFueling(lastKM || "0");
  }, [lastKM]);

  // Derive total amount
  const totalAmount = useMemo(() => {
    const qty = parseFloat(fuelQuantity) || 0;
    const price = parseFloat(fuelPrice) || 0;
    return (qty * price).toFixed(2);
  }, [fuelQuantity, fuelPrice]);

  const resetForm = useCallback(() => {
    setVendor(null);
    setFuelType(null);
    setCurrentKmFueling("");
    setLastKmFueling(lastKM || "0");
    setFuelQuantity("");
    setFuelPrice("");
    setAttachments([]);
    setAttachmentPreview(null);
    setErrorMsg({});
  }, [lastKM]);

  const handleClose = useCallback(() => {
    resetForm();
    onHide();
  }, [onHide, resetForm]);

  const validateForm = useCallback(() => {
    const errors = {};
    if (!vendor) errors.vendor_id = "Vendor is required";
    if (!fuelType) errors.fuel_type = "Fuel Type is required";
    if (!currentKmFueling) errors.current_km_fueling = "Current KM is required";
    if (!fuelQuantity) errors.fuel_quantity = "Fuel Quantity is required";
    if (!fuelPrice) errors.fuel_price = "Fuel Price is required";

    // VAPT: Basic logic validation
    if (parseFloat(currentKmFueling) < parseFloat(lastKmFueling)) {
      errors.current_km_fueling = "Current KM cannot be less than last KM";
    }

    setErrorMsg(errors);
    return Object.keys(errors).length === 0;
  }, [vendor, fuelType, currentKmFueling, lastKmFueling, fuelQuantity, fuelPrice]);

  const processFiles = useCallback((files) => {
    const allowedTypes = ["image/jpeg", "image/png", "application/pdf"];
    const maxSizeMB = 5;

    const validFiles = files.filter(file => {
      if (!validateFileType(file, allowedTypes)) {
        setErrorMsg(prev => ({ ...prev, attachments: "Invalid file type. Only JPG, PNG, and PDF allowed." }));
        return false;
      }
      if (!validateFileSize(file, maxSizeMB)) {
        setErrorMsg(prev => ({ ...prev, attachments: `File size exceeds ${maxSizeMB}MB limit.` }));
        return false;
      }
      return true;
    });

    const newAttachments = validFiles.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
      isImage: file.type.startsWith("image/"),
      isPdf: file.type === "application/pdf",
      name: file.name,
    }));

    setAttachments(prev => [...prev, ...newAttachments]);
    if (newAttachments.length > 0 && !attachmentPreview) {
      setAttachmentPreview(newAttachments[0].preview);
    }
  }, [attachmentPreview]);

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    processFiles(files);
    // Reset input value to allow selecting same file again
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleDragDrop = (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    processFiles(files);
  };

  const removeAttachment = useCallback((index) => {
    setAttachments(prev => {
      const updated = prev.filter((_, idx) => idx !== index);
      if (updated.length === 0) {
        setAttachmentPreview(null);
      } else if (attachmentPreview === prev[index].preview) {
        setAttachmentPreview(updated[0].preview);
      }
      return updated;
    });
  }, [attachmentPreview]);

  const saveAttachment = async (parentId) => {
    const fileToBase64 = (file) =>
      new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = (err) => reject(err);
        reader.readAsDataURL(file);
      });

    try {
      const imagesBase64 = await Promise.all(attachments.map((img) => fileToBase64(img.file)));
      const response = await saveFuelAttachmentApi(parentId, { attachment: imagesBase64 });
      if (response?.data?.success) {
        resetForm();
        onSubmit(response.data);
      }
    } catch (err) {
      setErrorMsg(prev => ({ ...prev, attachments: "Failed to upload attachments" }));
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    const payload = {
      vendor_id: vendor?.value,
      fuel_type: fuelType?.value,
      current_km_fueling: sanitizeInput(String(currentKmFueling)),
      last_km_fueling: sanitizeInput(String(lastKmFueling)),
      fuel_quantity: sanitizeInput(String(fuelQuantity)),
      fuel_price: sanitizeInput(String(fuelPrice)),
      total_amount: totalAmount,
      vehicle_id: id,
    };

    setIsLoading(true);
    try {
      const response = await submitVehicleFuelEntry(payload);
      if (response?.data?.success) {
        if (attachments.length === 0) {
          resetForm();
          onSubmit(response.data);
        } else {
          await saveAttachment(response.data.id);
        }
      } else if (response?.status === 400 && response?.data?.error) {
        const backendErrors = {};
        Object.entries(response.data.error).forEach(([key, value]) => {
          backendErrors[key] = Array.isArray(value) ? value[0] : value;
        });
        setErrorMsg(backendErrors);
      } else {
        onSubmit(response?.data || { success: false, message: "Submission failed" });
      }
    } catch (err) {
      setErrorMsg({ general: "A network error occurred. Please try again." });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} backdrop="static" size="md" centered className="fuel-entry-modal">
      <Modal.Header closeButton className="border-bottom-0 pb-0">
        <Modal.Title className="fw-700 fs-18 text-21272C">Add Fuel Cost</Modal.Title>
      </Modal.Header>

      <Modal.Body className="pt-3">
        <div className="mb-4">
          <label className="form-label fs-13 fw-600 text-start vehicle-label mb-2">Vendor</label>
          <CommonDropdown
            apiUrl={`${apiUrl}vendor/autosuggestion`}
            placeholder="Search and select vendor"
            value={vendor}
            onSelect={setVendor}
            handleClear={() => setVendor(null)}
          />
          {errorMsg.vendor_id && <small className="text-danger mt-1 d-block">{errorMsg.vendor_id}</small>}
        </div>

        <div className="row">
          <div className="col-md-6 mb-4">
            <label className="form-label fs-13 fw-600 mb-2">Fuel Type</label>
            <CommonStaticDropdown
              items={fuelTypeOptions}
              value={fuelType}
              onSelect={setFuelType}
              placeholder="Select fuel type"
            />
            {errorMsg.fuel_type && <small className="text-danger mt-1 d-block">{errorMsg.fuel_type}</small>}
          </div>
          <div className="col-md-6 mb-4">
            <label className="form-label fs-13 fw-600 mb-2">Current KM while Fueling</label>
            <input
              type="text"
              className={`form-control ${errorMsg.current_km_fueling ? 'is-invalid' : ''}`}
              placeholder="Enter current KM"
              value={currentKmFueling}
              onChange={(e) => setCurrentKmFueling(e.target.value.replace(/[^0-9.]/g, ""))}
            />
            {errorMsg.current_km_fueling && <small className="text-danger mt-1 d-block">{errorMsg.current_km_fueling}</small>}
          </div>
        </div>

        <div className="row">
          <div className="col-md-6 mb-4">
            <label className="form-label fs-13 fw-600 mb-2">Last KM while Fueling</label>
            <input
              type="text"
              className="form-control bg-light"
              value={lastKmFueling}
              readOnly
              style={{ cursor: "not-allowed" }}
            />
          </div>
          <div className="col-md-6 mb-4">
            <label className="form-label fs-13 fw-600 mb-2">Quantity (Ltrs)</label>
            <input
              type="text"
              className={`form-control ${errorMsg.fuel_quantity ? 'is-invalid' : ''}`}
              placeholder="Enter quantity"
              value={fuelQuantity}
              onChange={(e) => setFuelQuantity(e.target.value.replace(/[^0-9.]/g, ""))}
            />
            {errorMsg.fuel_quantity && <small className="text-danger mt-1 d-block">{errorMsg.fuel_quantity}</small>}
          </div>
        </div>

        <div className="row">
          <div className="col-md-6 mb-4">
            <label className="form-label fs-13 fw-600 mb-2">Fuel Price (per Ltr)</label>
            <div className="input-group">
              <span className="input-group-text bg-white border-end-0">₹</span>
              <input
                type="text"
                className={`form-control border-start-0 ${errorMsg.fuel_price ? 'is-invalid' : ''}`}
                placeholder="0.00"
                value={fuelPrice}
                onChange={(e) => setFuelPrice(e.target.value.replace(/[^0-9.]/g, ""))}
              />
            </div>
            {errorMsg.fuel_price && <small className="text-danger mt-1 d-block">{errorMsg.fuel_price}</small>}
          </div>

          <div className="col-md-6 mb-4">
            <label className="form-label fs-13 fw-600 mb-2">Total Amount</label>
            <div className="input-group">
              <span className="input-group-text bg-light border-end-0">₹</span>
              <input
                type="text"
                className="form-control border-start-0 bg-light"
                value={totalAmount}
                readOnly
                style={{ cursor: "not-allowed" }}
              />
            </div>
          </div>
        </div>

        <div className="mb-2">
          <label className="form-label fs-13 fw-600 mb-2">Attachment</label>
          <div
            className="rounded-3 p-4 d-flex flex-column align-items-center justify-content-center w-100 transition-all"
            style={{
              minHeight: 140,
              cursor: "pointer",
              border: "2px dashed #D1D5DB",
              backgroundColor: "#F9FAFB",
            }}
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleDragDrop}
            onMouseEnter={(e) => e.currentTarget.style.borderColor = "#3469E7"}
            onMouseLeave={(e) => e.currentTarget.style.borderColor = "#D1D5DB"}
          >
            {attachmentPreview ? (
              attachments.find((a) => a.preview === attachmentPreview)?.isPdf ? (
                <div className="text-center">
                  <FileText size={40} color="#3469E7" />
                  <p className="fs-12 text-dark mb-0 mt-1">
                    {attachments.find((a) => a.preview === attachmentPreview)?.name || "Document.pdf"}
                  </p>
                </div>
              ) : (
                <img
                  src={attachmentPreview}
                  alt="preview"
                  style={{ maxWidth: "100%", maxHeight: "120px", objectFit: "contain", borderRadius: "8px" }}
                />
              )
            ) : (
              <div className="text-center">
                <img src={uploadIcon} alt="upload" className="mb-2" style={{ width: 32, height: 32, opacity: 0.6 }} />
                <p className="fs-13 fw-500 text-dark mb-1">
                  Click to upload or <span className="text-primary">drag and drop</span>
                </p>
                <p className="text-muted fs-11 mb-0">PDF, PNG, JPG (max. 5MB)</p>
              </div>
            )}
          </div>
          {errorMsg.attachments && <small className="text-danger mt-2 d-block">{errorMsg.attachments}</small>}

          {attachments.length > 0 && (
            <div className="d-flex flex-wrap gap-2 mt-3">
              {attachments.map((att, idx) => (
                <div key={idx} className="position-relative" style={{ width: 64, height: 64 }}>
                  {att.isPdf ? (
                    <div
                      className="w-100 h-100 d-flex align-items-center justify-content-center rounded-2 border bg-light"
                      style={{ cursor: "pointer" }}
                      onClick={() => setAttachmentPreview(att.preview)}
                    >
                      <FileText size={24} color="#3469E7" />
                    </div>
                  ) : (
                    <img
                      src={att.preview}
                      alt="thumbnail"
                      className="w-100 h-100 object-fit-cover rounded-2 border"
                      style={{ cursor: "pointer" }}
                      onClick={() => setAttachmentPreview(att.preview)}
                    />
                  )}
                  <button
                    className="btn btn-sm btn-white position-absolute d-flex align-items-center justify-content-center shadow-sm"
                    style={{
                      top: -6,
                      right: -6,
                      width: 20,
                      height: 20,
                      padding: 0,
                      borderRadius: "50%",
                      border: "1px solid #E5E7EB",
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      removeAttachment(idx);
                    }}
                  >
                    <img src={closeAttachment} alt="remove" style={{ width: 10 }} />
                  </button>
                </div>
              ))}
            </div>
          )}
          <input ref={fileInputRef} type="file" multiple accept=".pdf,.png,.jpg,.jpeg" onChange={handleFileUpload} style={{ display: "none" }} />
        </div>
      </Modal.Body>

      <Modal.Footer className="border-top-0 pt-0 pb-4 px-4">
        <Button variant="white" onClick={handleClose} className="rounded-pill px-4 border fs-14 fw-600 me-2" style={{ color: "#4B5563" }}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          className="rounded-pill px-5 fs-14 fw-600 shadow-sm transition-all"
          disabled={isLoading}
          style={{ backgroundColor: "#3469E7", border: "none" }}
        >
          {isLoading ? (
            <div className="d-flex align-items-center gap-2">
              <Spinner animation="border" size="sm" />
              <span>Saving...</span>
            </div>
          ) : "Save Entry"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default FuelEntryModal;
